import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  UserListResponse,
  UserMutationResponse,
  UserResponse,
  UserRole,
  UserSignInMethod,
} from "@steelbill/types";
import { api } from "@/lib/api";
import { endpoints } from "@/lib/endpoints";
import { queryKeys } from "@/lib/query-keys";

/**
 * The accounts behind `user.manage` (spec §2, §13).
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * WHY THIS MODULE EXISTS. `user.manage` sat in the §2.1 capability union with no
 * route behind it until 2026-08-17, and the API half landed that morning with no
 * screen in front of it. Between the two, the only way to bring the CA's account
 * into existence was a hand-written `INSERT` against the production database —
 * a bcrypt hash computed by hand on a live tax system, and a role string with
 * nothing to validate it.
 * ═════════════════════════════════════════════════════════════════════════════
 *
 * NOTHING IN HERE DECIDES ANYTHING. Every rule the screen has to obey — which
 * credential a role takes, whether a role change would lock the last
 * administrator out, whether a password is long enough — is the server's, and
 * each one comes back as a status and a SENTENCE. This module's whole job is to
 * carry the request out and hand the sentence back unedited.
 *
 * A SECRET GOES IN AND NEVER COMES BACK. `UserResponse` has no hash and nothing
 * from which one could be reconstructed; `hasCredential` is a boolean ABOUT a
 * hash. Nothing here caches, stores or re-reads what was typed.
 */

/* -------------------------------------------------------------------------- */
/* Reading                                                                     */
/* -------------------------------------------------------------------------- */

/** The two filters `UserListQuerySchema` accepts, as the screen holds them. */
export interface UserFilters {
  role?: UserRole | undefined;
  /** `true` → only switched-on accounts, `false` → only switched-off ones. */
  active?: boolean | undefined;
}

/**
 * A page of accounts, oldest first — the server's order, untouched.
 *
 * BY ID ASCENDING IS THE SERVER'S CHOICE AND THE SCREEN MUST NOT RE-SORT. The
 * owner is #1, the son #2, the CA #3; a stable order means the list does not
 * reshuffle itself when somebody is renamed, which on a three-row table matters
 * more than recency does. Newest-first is for a log, and the log is `/audit`.
 *
 * `limit` IS EXPLICIT AND SITS UNDER THE ENDPOINT'S CEILING OF 100. There is no
 * paging control on this screen and there should not be one: the table holds
 * three rows and a "next page" that can never light up is a control that teaches
 * the reader to distrust the others.
 */
export function useUsers(filters: UserFilters = {}) {
  const query = {
    ...(filters.role === undefined ? {} : { role: filters.role }),
    ...(filters.active === undefined ? {} : { active: filters.active }),
    limit: 100,
  };

  return useQuery({
    queryKey: queryKeys.users.list(query),
    queryFn: async ({ signal }) =>
      api.get<UserListResponse>(endpoints.users, { query, signal }),
  });
}

/* -------------------------------------------------------------------------- */
/* Which door a role signs in through                                          */
/* -------------------------------------------------------------------------- */

/** What the screen has learned about each role's sign-in, from rows it has seen. */
export type KnownDoors = Partial<Record<UserRole, UserSignInMethod>>;

/**
 * Fold `signInWith` off a page of accounts into a role → door map.
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * THE CLIENT IS NOT ALLOWED TO KNOW THIS RULE, SO IT LEARNS IT INSTEAD.
 *
 * `ROLE_CREDENTIAL_PATH` lives in `apps/api/src/auth/credential-paths.ts` and
 * its header says why it is not in `@steelbill/types`: it is AUTHENTICATION, and
 * an authentication rule compiled into the SPA is a rule somebody will
 * eventually be tempted to enforce here, where it is decoration. So the server
 * computes the answer per ACCOUNT and puts it on the row as `signInWith`, and
 * this function is the only thing in the client that reads it.
 *
 * WHAT THAT COSTS, HONESTLY: a role with no account yet has no row to learn
 * from — which is precisely the ACCOUNTANT on the day this screen is needed
 * most. The create form asks in that one case rather than guessing (see
 * `UsersScreen`), and the server's 400 names the field it wanted if the answer
 * is wrong. Guessing from the role name would be `ROLE_CREDENTIAL_PATH`
 * reimplemented in a component, which is the thing that put the CA one careless
 * `UPDATE` away from a 6-digit PIN in the first place.
 *
 * IT ACCUMULATES ACROSS RESPONSES rather than being derived from the current
 * page, so filtering the list to one role does not make the create form forget
 * what it already knew about the others.
 * ═════════════════════════════════════════════════════════════════════════════
 */
export function learnDoors(known: KnownDoors, users: readonly UserResponse[]): KnownDoors {
  let changed = false;
  const next: KnownDoors = { ...known };

  for (const user of users) {
    if (next[user.role] === user.signInWith) continue;
    next[user.role] = user.signInWith;
    changed = true;
  }

  // Same object back when nothing moved, so an effect feeding this into state
  // cannot loop.
  return changed ? next : known;
}

/* -------------------------------------------------------------------------- */
/* Writing                                                                     */
/* -------------------------------------------------------------------------- */

/**
 * `POST /users`. The body is assembled by the FORM, not here, because which keys
 * are legal depends on the role's door and the server is the authority on that.
 *
 * Typed as a plain record for the reason `useSaveCompany` uses one:
 * `UserCreateSchema` is `.strict()` with four optional credential fields, so a
 * concrete TypeScript shape here would either permit the illegal combinations or
 * encode `ROLE_CREDENTIAL_PATH` in the client. The server validates the body it
 * is actually sent.
 */
export function useCreateUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (body: Record<string, unknown>) =>
      api.post<UserMutationResponse>(endpoints.users, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.users.all });
    },
  });
}

/**
 * `PUT /users/:id` — the display name, the role, and whether the account is on.
 *
 * IT CANNOT REACH A CREDENTIAL, and that is the server's `.strict()` schema
 * rather than this hook's discipline: a rename must never quietly reset a
 * password. Setting a secret is {@link useSetUserCredential}.
 *
 * NOTHING IS PRE-CHECKED HERE. The five refusals (403 self-demotion, 403
 * self-deactivation, 409 last administrator, 409 a door the account cannot open,
 * 409 an identifier already in use) are decided on the server against state the
 * browser does not hold — the count of other active administrators, above all.
 * A client-side copy would be a second opinion that is wrong exactly when it
 * matters.
 */
export function useUpdateUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, body }: { id: number; body: Record<string, unknown> }) =>
      api.put<UserMutationResponse>(endpoints.user(id), body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.users.all });
    },
  });
}

/**
 * `POST /users/:id/credential` — set or reset the secret for this account's door.
 *
 * IT IS ALSO THE ONLY UNLOCK THE API HAS: the reset clears `failedLoginCount`
 * and `lockedUntil` (§11A). There is deliberately no "unlock but keep the
 * password" route, because an account freezes precisely when somebody has been
 * guessing at it — so the screen must say that this is what unlocking looks
 * like, rather than hunting for a control that does not exist.
 *
 * THE SECRET IS NOT KEPT. It travels in the body and the mutation variables are
 * discarded with the mutation; nothing here writes one to a cache, a query key
 * or a log.
 */
export function useSetUserCredential() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, body }: { id: number; body: Record<string, unknown> }) =>
      api.post<UserMutationResponse>(endpoints.userCredential(id), body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.users.all });
    },
  });
}
